import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBreakpoint } from '../../../../shared/hooks/use-breakpoint';
import { usePagination } from '../../../../shared/hooks/use-pagination';
import { UserSettings } from '../../../user/models/user-settings';
import { useGetClientUsersQuery } from '../../api/client-users-api';
import ClientUserDeleteModal from '../client-user-delete-modal/client-user-delete-modal';
import ClientUserFormModal from '../client-user-form-modal/client-user-form-modal';
import ClientUserList from '../client-user-list/client-user-list';
import ClientUserPasswordResetModal from '../client-user-password-reset-modal/client-user-password-reset-modal';
import ClientUserSignatureResetModal from '../client-user-signature-reset-modal/client-user-signature-reset-modal';
import ClientUsersHeader from '../client-users-header/client-users-header';
import ClientUsersTable from '../client-users-table/client-users-table';
import styles from './client-users-content.module.css';

const ClientUsersContent = (): JSX.Element => {
  const screens = useBreakpoint();
  const [searchParams] = useSearchParams();

  const [pageSize, setPageSize] = useState<number>();

  const { data: clientUsers, isFetching: isClientDataFetching } = useGetClientUsersQuery(
    {
      page: Number(searchParams.get('page')) || 1,
      name: searchParams.get('name') || '',
      phone: searchParams.get('phone') || '',
      email: searchParams.get('email') || '',
      surname: searchParams.get('surname') || '',
      unit: searchParams.get('unit') || '',
      roles: searchParams.get('roles') || '',
      group: searchParams.get('groups') || '',
      username: searchParams.get('username') || '',
      ...(searchParams.has('signature') && {
        signature: searchParams.get('signature') === '1' ? '1' : '0',
      }),
      ...(searchParams.has('active') && {
        active: searchParams.get('active') === '1' ? '1' : '0',
      }),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      limit: pageSize!,
    },
    {
      skip: !pageSize,
    },
  );

  const [limit, pagination] = usePagination(clientUsers?.total, UserSettings.ClientUsersPageSize);

  const clientUsersDataSource = useMemo(() => {
    return (clientUsers && Array.isArray(clientUsers.items) ? clientUsers.items : []).map(userItem => {
      return {
        id: userItem.id,
        active: userItem.active,
        canChangePassword: userItem.canChangePassword,
        name: userItem.name,
        email: userItem.email,
        totpEnabled: userItem.totpEnabled,
        hasSignature: userItem.hasSignature,
        unit: userItem.unit,
        roles: userItem.roles,
        groups: userItem.groups.map(({ name }) => name).join(', '),
        username: userItem.username,
      };
    });
  }, [clientUsers]);

  useEffect(() => {
    setPageSize(limit);
  }, [limit]);

  return (
    <>
      <ClientUsersHeader pagination={pagination} />
      {screens.sm ? (
        <div className={styles.contentContainer}>
          <ClientUsersTable clientUsersDataSource={clientUsersDataSource} isClientDataFetching={isClientDataFetching} />
        </div>
      ) : (
        <ClientUserList clientUsersDataSource={clientUsersDataSource} isClientDataFetching={isClientDataFetching} />
      )}
      <ClientUserPasswordResetModal />
      <ClientUserSignatureResetModal />
      <ClientUserDeleteModal />
      <ClientUserFormModal />
    </>
  );
};
export default ClientUsersContent;
